import React from 'react';
import { osc, wobble, loop, clamp, lerp, easeOut } from '../../core/AnimationUtils';

/*
 * Concept-acting PHYSICS SCENES (Anivaze v2) — gravity and states of matter
 * ACT their concepts out at kindergarten level: an apple falls and bounces to
 * show acceleration; tiny particle dots transform between ice/water/steam to
 * show that heat makes things move faster. Pure function of `t`. Bright,
 * rounded, friendly, minimal words. See ConceptScenes.tsx for the reference
 * and ANIMATION-PATTERN.md for the motion contract.
 */

/* ━━━ Gravity: an apple falls from a tree, accelerating, and bounces ━━━━━━━ */
export interface GravitySceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

export const GravityScene: React.FC<GravitySceneProps> = ({
  opacity = 1,
  accentColor = '#ef4444',
  t = 0,
  label = 'What goes up must come down!',
}) => {
  // ── Apple fall cycle (~4s) ─────────────────────────────────────────────
  // phase 0→1 over 4s, looping
  const cycleDur = 4.0;
  const phase = loop(t, cycleDur);          // 0→1 looping

  // Apple sits on tree until phase ~0.1, then falls; phase 0.7-0.8 = bounce, 0.8-1 = rest
  const fallStart = 0.1;
  const groundHit = 0.65;
  const bounceEnd  = 0.8;

  // Tree anchor (where the apple starts)
  const treeX = 310;
  const appleStartY = 138;                  // hangs from tree bough
  const groundY = 370;                      // grass line

  // Fall progress: 0 (on tree) → 1 (ground)
  const rawFall = phase < fallStart ? 0
    : phase < groundHit ? (phase - fallStart) / (groundHit - fallStart)
    : 1;

  // Ease-in (quadratic) to clearly show acceleration
  const fallProgress = rawFall * rawFall;

  // Bounce: after hitting ground the apple pops up a little then settles
  const bouncePhase = phase > groundHit && phase < bounceEnd
    ? (phase - groundHit) / (bounceEnd - groundHit)
    : 0;
  const bounceOffset = bouncePhase > 0
    ? -Math.sin(bouncePhase * Math.PI) * 38   // single arc upward
    : 0;

  const appleY = phase < fallStart
    ? appleStartY                             // resting on tree
    : lerp(appleStartY, groundY - 14, clamp(fallProgress, 0, 1)) + bounceOffset;

  const appleX = treeX + wobble(t, 3.8, phase < fallStart ? 1.5 : 0);

  // Speed lines: only visible when apple is moving fast (lower 40% of fall)
  const speedProgress = clamp((fallProgress - 0.6) / 0.4, 0, 1);
  const showSpeedLines = phase >= fallStart && phase < groundHit && speedProgress > 0;

  // Little character to the right watches the apple, tilts head up when apple falls
  const charX = 530;
  const charY = 370;
  const charLookUp = phase > fallStart && phase < groundHit + 0.05;
  const headTilt = charLookUp ? -18 : wobble(t, 2.3, 3);
  const eyeBrowRaise = charLookUp ? -5 : 0;

  // Sun gentle pulse
  const sunR = 28 + osc(t, 4, 0, 2);

  // Clouds drift
  const cloud1X = ((t * 12 + 0)   % 930) - 110;
  const cloud2X = ((t * 8  + 350) % 930) - 110;

  // Leaves on tree sway softly
  const leafSway = wobble(t, 2.5, 4);

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="gv-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor="#e0f2fe" />
        </linearGradient>
        <radialGradient id="gv-sun" cx="50%" cy="50%">
          <stop offset="0%"   stopColor="#fff9c4" />
          <stop offset="70%"  stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
        <radialGradient id="gv-apple" cx="40%" cy="35%">
          <stop offset="0%"   stopColor="#fca5a5" />
          <stop offset="100%" stopColor={accentColor} />
        </radialGradient>
      </defs>

      {/* Sky */}
      <rect x={0} y={0} width={800} height={450} fill="url(#gv-sky)" />

      {/* Grass */}
      <rect x={0} y={370} width={800} height={80} fill="#4ade80" />
      <ellipse cx={160} cy={372} rx={200} ry={30} fill="#22c55e" opacity={0.5} />
      <ellipse cx={640} cy={372} rx={220} ry={28} fill="#22c55e" opacity={0.45} />

      {/* Sun */}
      <g transform="translate(720 68)">
        <g transform={`rotate(${(t * 6) % 360})`} opacity={0.85}>
          {Array.from({ length: 10 }, (_, k) => {
            const a = (k / 10) * Math.PI * 2;
            const r1 = sunR + 8, r2 = sunR + 20;
            return <line key={k} x1={r1 * Math.cos(a)} y1={r1 * Math.sin(a)} x2={r2 * Math.cos(a)} y2={r2 * Math.sin(a)} stroke="#fcd34d" strokeWidth={3} strokeLinecap="round" />;
          })}
        </g>
        <circle r={sunR} fill="url(#gv-sun)" />
        {/* sun face */}
        <circle cx={-8} cy={-6} r={4} fill="#92400e" />
        <circle cx={8}  cy={-6} r={4} fill="#92400e" />
        <path d="M-8,6 Q0,14 8,6" fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
      </g>

      {/* Drifting clouds */}
      <g transform={`translate(${cloud1X} 90)`} opacity={0.92}>
        <ellipse cx={0}   cy={0}  rx={38} ry={22} fill="#fff" />
        <ellipse cx={38}  cy={6}  rx={30} ry={18} fill="#fff" />
        <ellipse cx={-32} cy={8}  rx={26} ry={16} fill="#fff" />
      </g>
      <g transform={`translate(${cloud2X} 148)`} opacity={0.85}>
        <ellipse cx={0}   cy={0}  rx={30} ry={18} fill="#fff" />
        <ellipse cx={30}  cy={5}  rx={24} ry={14} fill="#fff" />
        <ellipse cx={-24} cy={6}  rx={20} ry={12} fill="#fff" />
      </g>

      {/* ── Tree ─────────────────────────────────────────────────────────── */}
      {/* Trunk */}
      <rect x={treeX - 14} y={240} width={28} height={130} rx={10} fill="#a16207" />
      {/* Main canopy */}
      <g transform={`rotate(${leafSway} ${treeX} 200)`}>
        <ellipse cx={treeX}      cy={190} rx={90}  ry={80}  fill="#16a34a" />
        <ellipse cx={treeX - 60} cy={220} rx={60}  ry={55}  fill="#15803d" />
        <ellipse cx={treeX + 58} cy={215} rx={62}  ry={52}  fill="#15803d" />
        {/* highlight spots */}
        <ellipse cx={treeX - 20} cy={165} rx={30} ry={22} fill="#4ade80" opacity={0.4} />
      </g>

      {/* Apple resting on bough (only when phase < fallStart) */}
      {phase < fallStart && (
        <circle cx={treeX} cy={appleStartY} r={16} fill="url(#gv-apple)" stroke="#b91c1c" strokeWidth={2} />
      )}
      {/* Apple stem */}
      {phase < fallStart && (
        <line x1={treeX} y1={appleStartY - 16} x2={treeX + 4} y2={appleStartY - 26} stroke="#15803d" strokeWidth={3} strokeLinecap="round" />
      )}

      {/* Speed lines (appear as apple accelerates near ground) */}
      {showSpeedLines && Array.from({ length: 4 }, (_, k) => (
        <line
          key={k}
          x1={appleX - 22 + k * 4} y1={appleY - 22 - k * 7}
          x2={appleX - 22 + k * 4} y2={appleY - 38 - k * 7}
          stroke="#fbbf24" strokeWidth={2.5 - k * 0.4} strokeLinecap="round"
          opacity={speedProgress * (1 - k * 0.2)}
        />
      ))}

      {/* Falling / bouncing apple */}
      {phase >= fallStart && (
        <g>
          <ellipse cx={appleX} cy={groundY - 4} rx={14} ry={5} fill="rgba(0,0,0,0.18)" />
          <circle cx={appleX} cy={appleY} r={16} fill="url(#gv-apple)" stroke="#b91c1c" strokeWidth={2} />
          {/* stem */}
          <line x1={appleX} y1={appleY - 16} x2={appleX + 4} y2={appleY - 26} stroke="#15803d" strokeWidth={3} strokeLinecap="round" />
          {/* shine */}
          <circle cx={appleX - 5} cy={appleY - 6} r={4} fill="rgba(255,255,255,0.45)" />
        </g>
      )}

      {/* ── Little character ─────────────────────────────────────────────── */}
      <g transform={`translate(${charX} ${charY})`}>
        {/* shadow */}
        <ellipse cx={0} cy={2} rx={22} ry={7} fill="rgba(0,0,0,0.15)" />
        {/* body */}
        <ellipse cx={0} cy={-32} rx={22} ry={28} fill="#fde68a" stroke="#d97706" strokeWidth={2} />
        {/* arms */}
        <line x1={-22} y1={-38} x2={-38} y2={-28} stroke="#d97706" strokeWidth={4} strokeLinecap="round" />
        <line x1={22}  y1={-38} x2={38}  y2={-28} stroke="#d97706" strokeWidth={4} strokeLinecap="round" />
        {/* legs */}
        <line x1={-10} y1={-6}  x2={-14} y2={18}  stroke="#d97706" strokeWidth={4} strokeLinecap="round" />
        <line x1={10}  y1={-6}  x2={14}  y2={18}   stroke="#d97706" strokeWidth={4} strokeLinecap="round" />
        {/* head */}
        <g transform={`rotate(${headTilt} 0 -58)`}>
          <circle cx={0} cy={-72} r={24} fill="#fde68a" stroke="#d97706" strokeWidth={2} />
          {/* eyebrows raised when looking up */}
          <line x1={-12} y1={-80 + eyeBrowRaise} x2={-6}  y2={-82 + eyeBrowRaise} stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
          <line x1={6}   y1={-82 + eyeBrowRaise} x2={12}  y2={-80 + eyeBrowRaise} stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
          {/* eyes */}
          <circle cx={-9} cy={-72} r={4} fill="#1f2937" />
          <circle cx={9}  cy={-72} r={4} fill="#1f2937" />
          {/* shine in eyes */}
          <circle cx={-7} cy={-74} r={1.5} fill="#fff" />
          <circle cx={11} cy={-74} r={1.5} fill="#fff" />
          {/* mouth — open in surprise when apple falls */}
          {charLookUp
            ? <ellipse cx={0} cy={-62} rx={6} ry={7} fill="#1f2937" />
            : <path d="M-8,-62 Q0,-55 8,-62" fill="none" stroke="#92400e" strokeWidth={2.5} strokeLinecap="round" />
          }
        </g>
      </g>

      {/* Caption */}
      <g transform="translate(400 425)">
        <rect x={-230} y={-22} width={460} height={40} rx={20} fill="rgba(255,255,255,0.88)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle" fill="#0f172a" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ States of Matter: particles transform ice → water → steam over ~9s ━━ */
export interface StatesOfMatterSceneProps {
  opacity?: number;
  accentColor?: string;
  /** Short friendly caption. */
  label?: string;
  /** Seconds since the scene started. */
  t?: number;
}

export const StatesOfMatterScene: React.FC<StatesOfMatterSceneProps> = ({
  opacity = 1,
  accentColor = '#3b82f6',
  t = 0,
  label = 'Heat makes tiny bits zoom faster!',
}) => {
  // ── Three-state cycle: 0→3s solid, 3→6s liquid, 6→9s gas, then loops ──
  const cycleDur = 9.0;
  const cycleT = t % cycleDur;
  // stateP: 0 = solid, 1 = liquid, 2 = gas (with smooth transitions)
  const solidEnd   = 3.0;
  const liquidEnd  = 6.0;
  // gasEnd = cycleDur
  const transitionWidth = 0.8;  // seconds to blend between states

  // smoothState: 0 → pure solid, 1 → pure liquid, 2 → pure gas
  const solidToLiquid = clamp((cycleT - solidEnd) / transitionWidth, 0, 1);
  const liquidToGas   = clamp((cycleT - liquidEnd) / transitionWidth, 0, 1);
  // blend: 0=solid, 1=liquid, 2=gas (continuous)
  const stateBlend = solidToLiquid + liquidToGas;  // 0→2

  const isSolid  = stateBlend < 0.5;
  const isGas    = stateBlend > 1.5;

  // Flame intensity: grows from phase 0→1 solid→liquid→gas
  const flameHeight = lerp(28, 68, clamp(stateBlend / 2, 0, 1));
  const flameWobble = wobble(t, 0.35, 8) + wobble(t, 0.21, 4);

  // Pot dimensions
  const potX = 400;
  const potY = 320;
  const potW = 200;
  const potH = 110;
  const rimY = potY - potH / 2 + 10;  // top of liquid region inside pot

  // Inside pot: particles live in [potX±80, rimY+5 → potY+potH/2-10]
  const innerLeft  = potX - 85;
  const innerRight = potX + 85;
  const innerTop   = rimY + 5;
  const innerBot   = potY + potH / 2 - 14;
  const innerH     = innerBot - innerTop;
  const innerW     = innerRight - innerLeft;

  // 10 particles, each with a stable index-derived base position
  const PARTICLES = 10;
  const particles = Array.from({ length: PARTICLES }, (_, i) => {
    const baseX = innerLeft + (i / (PARTICLES - 1)) * innerW;
    // packed row positions for solid (two rows)
    const row = i < 5 ? 0 : 1;
    const col = i < 5 ? i : i - 5;
    const solidX = innerLeft + 22 + col * 32;
    const solidY = innerTop + innerH - 20 - row * 28;

    // liquid positions: spread out more, slosh side-to-side
    const liquidXBase = innerLeft + 18 + ((i * 163) % (innerW - 36));  // spread pseudo-randomly via index
    const liquidYBase = innerTop + 20 + ((i * 97)  % (innerH - 40));
    // fast oscillation in liquid — each particle has own period derived from index
    const liqPeriod = 0.6 + (i % 4) * 0.12;
    const liquidX = liquidXBase + wobble(t, liqPeriod, 14, i * 1.1);
    const liquidY = liquidYBase + wobble(t, liqPeriod * 1.3, 10, i * 0.9);

    // gas positions: particles drift upward and spread widely
    const gasPeriod = 0.4 + (i % 5) * 0.09;
    const gasBaseX = potX - 120 + ((i * 53) % 240);
    // gas particles rise above the pot
    const gasRiseOffset = (loop(t + i * 0.7, 2.5 + (i % 3) * 0.4)) * 160;
    const gasX = gasBaseX + wobble(t, gasPeriod, 22, i * 0.7);
    const gasY = innerBot - gasRiseOffset;

    // Interpolate position based on stateBlend
    let px: number, py: number;
    if (stateBlend <= 1) {
      // solid → liquid blend
      px = lerp(solidX, liquidX, easeOut(clamp(stateBlend, 0, 1)));
      py = lerp(solidY, liquidY, easeOut(clamp(stateBlend, 0, 1)));
    } else {
      // liquid → gas blend
      px = lerp(liquidX, gasX, easeOut(clamp(stateBlend - 1, 0, 1)));
      py = lerp(liquidY, gasY, easeOut(clamp(stateBlend - 1, 0, 1)));
    }

    // vibration amplitude grows with temperature (stateBlend)
    const vibAmp = 1.5 + stateBlend * 3;
    const vibPeriod = 0.18 - stateBlend * 0.04;   // faster as it heats
    const vx = isSolid ? wobble(t, Math.max(0.08, vibPeriod), vibAmp, i * 1.4) : 0;
    const vy = isSolid ? wobble(t, Math.max(0.08, vibPeriod * 1.1), vibAmp * 0.7, i * 1.2) : 0;

    // Particle color shifts: ice-blue → water-blue → steam-white
    const solidColor  = '#bae6fd';
    const liquidColor = accentColor;
    const gasColor    = '#e0f2fe';
    const particleColor = stateBlend < 1
      ? solidColor   // keep solid color in solid phase (lerp difficult in SVG, keep simple)
      : stateBlend < 1.5
        ? liquidColor
        : gasColor;

    return { px: px + vx, py: py + vy, particleColor, opacity: isGas ? clamp(1 - (py - innerTop + 160) / 200, 0.1, 1) : 1 };
  });

  // Bubbles rising in liquid phase
  const showBubbles = stateBlend > 0.5 && stateBlend < 1.8;
  const bubbles = showBubbles ? Array.from({ length: 4 }, (_, i) => {
    const bx = innerLeft + 30 + i * 42;
    const bPhase = loop(t + i * 0.55, 1.4);
    const by = lerp(innerBot, innerTop - 10, easeOut(bPhase));
    const br = 5 + i * 1.5;
    return { bx, by, br, bOpacity: clamp(1 - bPhase * 0.8, 0.2, 0.9) };
  }) : [];

  // Steam puffs above the pot in gas phase
  const steamVisible = stateBlend > 1.2;

  // Label for current state
  const stateLabel = isSolid ? 'Ice ❄' : isGas ? 'Steam ☁' : 'Water 💧';
  const stateLabelColor = isSolid ? '#0ea5e9' : isGas ? '#94a3b8' : accentColor;

  // Pot face (it gets happier as it boils)
  const potSmileAmt = clamp(stateBlend / 2, 0, 1);

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="sm-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
        <linearGradient id="sm-pot" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
        <radialGradient id="sm-glow" cx="50%" cy="100%">
          <stop offset="0%"   stopColor="#fbbf24" stopOpacity={0.7} />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
        </radialGradient>
        <clipPath id="sm-inner-clip">
          <rect x={innerLeft} y={innerTop} width={innerW} height={innerH + 10} rx={4} />
        </clipPath>
      </defs>

      {/* Background */}
      <rect x={0} y={0} width={800} height={450} fill="url(#sm-bg)" />

      {/* Warm glow on the table under the pot */}
      <ellipse cx={potX} cy={potY + 80} rx={130} ry={40} fill="url(#sm-glow)" />

      {/* ── Flame ──────────────────────────────────────────────────────── */}
      {/* Outer flame */}
      <path
        d={`M${potX - 30},${potY + potH / 2 + 10} Q${potX + flameWobble},${potY + potH / 2 + 10 - flameHeight * 0.6} ${potX},${potY + potH / 2 + 10 - flameHeight} Q${potX - flameWobble},${potY + potH / 2 + 10 - flameHeight * 0.6} ${potX + 30},${potY + potH / 2 + 10} Z`}
        fill="#f97316" opacity={0.9}
      />
      {/* Inner flame */}
      <path
        d={`M${potX - 16},${potY + potH / 2 + 8} Q${potX + flameWobble * 0.6},${potY + potH / 2 + 8 - flameHeight * 0.5} ${potX},${potY + potH / 2 + 8 - flameHeight * 0.82} Q${potX - flameWobble * 0.6},${potY + potH / 2 + 8 - flameHeight * 0.5} ${potX + 16},${potY + potH / 2 + 8} Z`}
        fill="#fcd34d" opacity={0.95}
      />
      {/* Flame core */}
      <path
        d={`M${potX - 7},${potY + potH / 2 + 6} Q${potX},${potY + potH / 2 + 6 - flameHeight * 0.55} ${potX + 7},${potY + potH / 2 + 6} Z`}
        fill="#fef9c3"
      />

      {/* ── Pot body ───────────────────────────────────────────────────── */}
      {/* Pot shadow */}
      <ellipse cx={potX} cy={potY + potH / 2 + 12} rx={110} ry={14} fill="rgba(0,0,0,0.35)" />
      {/* Pot shape */}
      <path
        d={`M${potX - potW / 2},${potY - potH / 2 + 16} Q${potX - potW / 2 - 10},${potY} ${potX - potW / 2},${potY + potH / 2} L${potX + potW / 2},${potY + potH / 2} Q${potX + potW / 2 + 10},${potY} ${potX + potW / 2},${potY - potH / 2 + 16} Z`}
        fill="url(#sm-pot)" stroke="#1e293b" strokeWidth={3}
      />
      {/* Pot rim */}
      <rect x={potX - potW / 2 - 12} y={potY - potH / 2 + 4} width={potW + 24} height={22} rx={11} fill="#475569" stroke="#1e293b" strokeWidth={2} />
      {/* Handles */}
      <path d={`M${potX - potW / 2 - 10},${potY - 8} Q${potX - potW / 2 - 42},${potY - 8} ${potX - potW / 2 - 42},${potY + 16} Q${potX - potW / 2 - 42},${potY + 36} ${potX - potW / 2 - 10},${potY + 36}`} fill="none" stroke="#475569" strokeWidth={12} strokeLinecap="round" />
      <path d={`M${potX + potW / 2 + 10},${potY - 8} Q${potX + potW / 2 + 42},${potY - 8} ${potX + potW / 2 + 42},${potY + 16} Q${potX + potW / 2 + 42},${potY + 36} ${potX + potW / 2 + 10},${potY + 36}`} fill="none" stroke="#475569" strokeWidth={12} strokeLinecap="round" />

      {/* ── Particles inside pot (clipped) ────────────────────────────── */}
      <g clipPath="url(#sm-inner-clip)">
        {particles.map(({ px, py, particleColor, opacity: pOp }, i) => (
          <circle key={i} cx={px} cy={py} r={9} fill={particleColor} stroke="rgba(255,255,255,0.3)" strokeWidth={1.5} opacity={pOp} />
        ))}
        {/* Bubbles */}
        {bubbles.map(({ bx, by, br, bOpacity }, i) => (
          <circle key={`b${i}`} cx={bx} cy={by} r={br} fill="none" stroke="#93c5fd" strokeWidth={1.5} opacity={bOpacity} />
        ))}
      </g>

      {/* Steam puffs rising above pot */}
      {steamVisible && Array.from({ length: 5 }, (_, i) => {
        const sfPhase = loop(t + i * 0.6, 2.2);
        const sx = potX - 60 + i * 30 + wobble(t, 1.1, 14, i * 0.8);
        const sy = potY - potH / 2 - 20 - sfPhase * 100;
        const sr = lerp(8, 22, sfPhase);
        return (
          <circle key={i} cx={sx} cy={sy} r={sr} fill="#e2e8f0" opacity={clamp((1 - sfPhase) * 0.65, 0, 0.65)} />
        );
      })}

      {/* Pot face — delighted as it boils */}
      <g transform={`translate(${potX} ${potY + 10})`}>
        {/* eyes */}
        <circle cx={-22} cy={-6} r={6}  fill="#fff" />
        <circle cx={22}  cy={-6} r={6}  fill="#fff" />
        <circle cx={-22} cy={-6} r={3.5} fill="#1e293b" />
        <circle cx={22}  cy={-6} r={3.5} fill="#1e293b" />
        {/* blush */}
        <ellipse cx={-34} cy={3}  rx={8}  ry={5}  fill="#fca5a5" opacity={0.7} />
        <ellipse cx={34}  cy={3}  rx={8}  ry={5}  fill="#fca5a5" opacity={0.7} />
        {/* mouth — grows into a grin as it heats */}
        <path d={`M${-14 - potSmileAmt * 2},${8} Q0,${12 + potSmileAmt * 14} ${14 + potSmileAmt * 2},${8}`}
          fill="none" stroke="#1e293b" strokeWidth={3} strokeLinecap="round" />
      </g>

      {/* ── State labels below the pot ─────────────────────────────────── */}
      <g transform={`translate(400 420)`}>
        {/* Full progression label */}
        <rect x={-200} y={-20} width={400} height={38} rx={19} fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth={1.5} />
        {/* Three segments */}
        {[
          { text: 'Ice', x: -130, active: stateBlend < 0.8,  color: '#7dd3fc' },
          { text: '→',  x: -80,  active: true,               color: '#94a3b8' },
          { text: 'Water', x: -20, active: stateBlend >= 0.5 && stateBlend < 1.8, color: accentColor },
          { text: '→',  x: 40,   active: true,               color: '#94a3b8' },
          { text: 'Steam', x: 112, active: stateBlend > 1.2,  color: '#e2e8f0' },
        ].map(({ text, x, active, color }) => (
          <text key={text} x={x} y={8} textAnchor="middle" fill={color} fontSize={15} fontWeight={active ? 800 : 500}
            fontFamily="Inter,sans-serif" opacity={active ? 1 : 0.45}>{text}</text>
        ))}
      </g>

      {/* Current state badge */}
      <g transform={`translate(${potX} ${potY - potH / 2 - 50})`}>
        <rect x={-60} y={-20} width={120} height={36} rx={18} fill={stateLabelColor} opacity={0.9} />
        <text x={0} y={6} textAnchor="middle" fill="#fff" fontSize={14} fontWeight={700} fontFamily="Inter,sans-serif">{stateLabel}</text>
      </g>

      {/* Friendly caption */}
      <g transform="translate(400 40)">
        <rect x={-260} y={-22} width={520} height={40} rx={20} fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle" fill="#f8fafc" fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
